/* eslint-disable no-empty-pattern */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable security/detect-object-injection */
/* eslint-disable import/no-unresolved */
// src/validations/leadValidationSchema.ts
import * as Yup from "yup";
import moment from "moment";
import regex from "src/helpers/regex";
import { partners } from "src/config/constant";
import { calcAgeFromToday } from "../ProposalDetails/resources";
import { CreateLeadsDetails, IUploadFiles, uploadFilesMandatoryFor } from "./interfaces";
import { products } from "../resources";
import { PRODUCT_ID } from "../ProposalDetails/interfaces";

export const fileInitialValue: IUploadFiles = {
  documentType: "",
  file: {}
};

export const initialCreateLeadsValues: CreateLeadsDetails = {
  productId: null,
  applicationNumber: "",
  bankBranchName: "",
  loanType: "",
  loanAccountNo: "",
  channel: "",
  channelCode: "",
  partnerId: "",
  bankVertical: "",
  solId: "",
  lgCode: "",
  firstName: "",
  middleName: "",
  lastName: "",
  customerDOB: "",
  customerMobileNumber: "",
  customerPAN: "",
  customerEmail: "",
  customerGender: "",
  applicationSignedDate: "",
  loanAmount: null,
  loanTerm: "",
  policyTerm: "",
  premiumPaymentTerm: null,
  premiumPaymentTermCode: "",
  loanInterestRate: "",
  sumAssured: null,
  files: [...[fileInitialValue]]
};

const leadValidationSchema = (loanAmt: any, role: string) =>
  Yup.object().shape<Record<keyof CreateLeadsDetails, Yup.AnySchema>>({
    productId: Yup.number().required("Product is Required"),
    applicationNumber: Yup.string()
      .required("Application No. is Required")
      .matches(regex.numeric, "Invalid Application No.")
      .min(9, "Min 9 digits allowed")
      .max(9, "Max 9 digits allowed"),
    loanType: Yup.string().required("Loan Type is Required"),
    loanAccountNo: Yup.string()
      .required("Loan A/C No is Required")
      .matches(regex.loanAcNo, "Invalid Loan A/C No")
      .max(20, "Max 20 digits allowed"),
    channel: Yup.string().required("Channel is Required"),
    bankBranchName: Yup.string().required("Distributor Name is Required"),
    bankVertical: Yup.string().when("channelCode", {
      is: "BA",
      then: (schema) => schema.required("Bank Vertical is Required"),
      otherwise: (schema) => schema.notRequired()
    }),
    solId: Yup.string()
      .required("Sol Id is Required")
      .matches(regex.alphaNumeric, "Enter valid value")
      .min(1, "Enter Min 1 digit value")
      .max(10, "Enter Max 10 digit value"),
    lgCode: Yup.string().when(["partnerId"], ([partnerId], schema) =>
      partnerId === partners.FEDERAL.toUpperCase()
        ? schema
            .required("Lg Code is Required")
            .matches(regex.alphaNumeric, "Enter valid value")
            .min(3, "Enter Min 3 digit value")
            .max(15, "Enter Max 15 digit value")
        : schema
            .required("Lg Code is Required")
            .min(3, "Enter Min 3 digit value")
            .max(15, "Enter Max 15 digit value")
    ),
    firstName: Yup.string()
      .required("First Name is Required")
      .matches(regex.nomineefullNamewithDot, "Invalid First Name")
      .min(1, "Min 1 characters Required")
      .max(60, "Max 60 characters Required"),
    middleName: Yup.string()
      .matches(regex.nomineefullNamewithDot, "Invalid Middle Name")
      .min(1, "Min 1 characters Required")
      .max(60, "Max 60 characters Required"),
    lastName: Yup.string()
      .matches(regex.nomineefullNamewithDot, "Invalid Last Name")
      .min(1, "Min 1 characters Required")
      .max(60, "Max 60 characters Required"),
    customerDOB: Yup.string()
      .required("DOB is Required")
      .test("length-check", "Invalid DOB", function lengthCheck(value) {
        if (!value) {
          return false;
        }
        const productId = this.parent?.productId ?? null;
        const age = calcAgeFromToday(value);
        if (productId === products.GHS_IDBI) {
          return age >= 18 && age <= 71 && value.length === 10;
        }
        if (productId === products.GHS_IDBI) {
          return age >= 18 && age <= 71 && value.length === 10;
        }
        if (productId === PRODUCT_ID.GCS) {
          return age >= 15 && age < 66 && value?.length === 10;
        }
        return age >= 18 && age <= 65 && value.length === 10;
      })
      .test("is-valid-date", "Invalid DOB", (value) => moment(value, "DD/MM/YYYY").isValid())
      .default(""),
    customerMobileNumber: Yup.string()
      .required("Mobile Number is Required")
      .matches(regex.numeric, "Invalid Mobile Number")
      .min(10, "Min 10 digits allowed")
      .max(16, "Max 16 digits allowed"),
    customerPAN: Yup.string()
      .matches(regex.pancard, "Invalid PAN")
      .max(10, "Max 10 values allowed"),
    customerEmail: Yup.string()
      .matches(regex.email, "Invalid Email")
      .max(60, "Max 60 value required"),
    customerGender: Yup.string().trim().required("Gender is Required"),
    applicationSignedDate: Yup.string()
      .required("Application Signed Date is Required")
      .test("length", "Application Signed Date Invalid ", (value) => value.length === 10)
      .test("is-valid-date", "Application Signed Date Invalid", (value) =>
        moment(value, "DD/MM/YYYY").isValid()
      )
      .default(""),
    channelCode: Yup.string(),
    loanAmount: Yup.string()
      .required("Loan Amount is Required")
      .matches(regex.digits, "Invalid Loan Amount")
      .max(10, "Max 10 digits allowed")
      .test("min-value", `Loan Amount must be at least ${loanAmt?.min}`, (value: any) => {
        const num = Number(value);
        return num >= loanAmt?.min;
      })
      .test("max-value", `Loan Amount cannot exceed ${loanAmt?.max}`, (value: any) => {
        const num = Number(value);
        return num <= loanAmt?.max;
      }),
    loanTerm: Yup.string()
      .required("Loan Term is Required")
      .matches(regex.digits, "Invalid Loan Amount")
      .max(3, "Max 3 digits allowed"),
    policyTerm: Yup.string().required("Policy Term is Required"),
    premiumPaymentTerm: Yup.string().required("Premium Payment Term is Required"),
    loanInterestRate: Yup.string()
      .matches(regex.digitsUpto2Precision, "Invalid Loan Interest Rate")
      .required("Loan Interest Rate is Required"),
    premiumPaymentTermCode: Yup.string(),
    files: Yup.array()
      .of(
        Yup.object().shape<Record<keyof IUploadFiles, Yup.AnySchema>>({
          documentType: Yup.string().when([], ([], schema) =>
            uploadFilesMandatoryFor(role)
              ? schema.required("Document type is required field")
              : schema?.notRequired()
          ),
          file: Yup.mixed<File>().when([], ([], schema) =>
            uploadFilesMandatoryFor(role)
              ? schema
                  .required("Please select a file")
                  .test(
                    "fileSelected",
                    "Please select a file",
                    (value) => value != null && !!value.name
                  )
              : schema?.notRequired()
          )
        })
      )
      .when([], ([], schema) =>
        uploadFilesMandatoryFor(role)
          ? schema.required("This is required field")
          : schema?.notRequired()
      ),
    sumAssured: Yup.string()
      .nullable()
      .when(["loanAmount"], ([loanAmount], schema) => {
        if (loanAmount !== undefined && loanAmount !== null) {
          return schema
            .required("Sum assured is required")
            .matches(regex.digits, "Invalid Sum Assured")
            .test(
              "max-sumAssured",
              "Sum assured must be less than or equal to loan amount",
              (value) => {
                if (!value) return true;
                return Number(value) <= Number(loanAmount);
              }
            )
            .test("min-sumAssured", "Sum assured must be at least 1", (value) => {
              if (!value) return true;
              return Number(value) >= 1;
            })
            .max(10, "Max 10 digits allowed");
        }
        return schema;
      })
  });

export const constructDEPayload = (
  values: typeof initialCreateLeadsValues,
  genderDataList: Record<string, any>
) => ({
  productId: values?.productId,
  applicationNumber: values?.applicationNumber,
  loanType: values?.loanType,
  loanAccountNo: values?.loanAccountNo,
  channel: values?.channelCode,
  partnerId: values.partnerId.toLowerCase(),
  bankBranchName: values.bankBranchName,
  bankVertical: values?.bankVertical,
  solId: values?.solId,
  lgCode: values?.lgCode,
  firstName: values?.firstName,
  middleName: values?.middleName,
  lastName: values?.lastName,
  customerDOB: moment(values?.customerDOB, "DD/MM/YYYY").format("YYYY-MM-DD"),
  customerMobileNumber: values?.customerMobileNumber,
  customerPAN: values?.customerPAN,
  customerEmail: values?.customerEmail,
  customerGender:
    Object.keys(genderDataList).find(
      (gender) => genderDataList[gender] === values.customerGender
    ) || "",
  applicationSignedDate: moment(values?.applicationSignedDate, "DD/MM/YYYY").format("YYYY-MM-DD"),
  loanAmount: Number(values?.loanAmount),
  loanTerm: values?.loanTerm,
  policyTerm: values?.policyTerm,
  premiumPaymentTerm: Number(values?.premiumPaymentTermCode),
  loanInterestRate: values?.loanInterestRate,
  sumAssured: values.loanAmount ? values.loanAmount : values.sumAssured
});

export default leadValidationSchema;
